import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, Mail, Loader2, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { verificationApi } from '../services/api';

interface VerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'sms' | 'email';
  onSuccess: () => void;
}

const VerificationModal: React.FC<VerificationModalProps> = ({
  isOpen,
  onClose,
  type,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<'send' | 'verify' | 'success'>('send');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [target, setTarget] = useState('');
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (isOpen) {
      setStep('send');
      setCode(['', '', '', '', '', '']);
      setError('');
      setTarget('');
      setCountdown(0);
    }
  }, [isOpen]);

  const handleSendCode = async () => {
    setSending(true);
    setError('');

    try {
      const result = await verificationApi.sendCode(type);
      if (result.success && result.data) {
        setTarget(result.data.target);
        setStep('verify');
        setCountdown(60);
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else {
        setError(result.error || 'Kod gonderilemedi');
      }
    } catch {
      setError('Bir hata olustu');
    } finally {
      setSending(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    await handleSendCode();
  };

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (newCode.every((digit) => digit !== '') && index === 5) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);
    if (pastedData.length === 6) {
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (codeStr?: string) => {
    const verifyCode = codeStr || code.join('');
    if (verifyCode.length !== 6) {
      setError('Lutfen 6 haneli kodu girin');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await verificationApi.verifyCode(type, verifyCode);
      if (result.success) {
        setStep('success');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      } else {
        setError(result.error || 'Kod dogrulanamadi');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch {
      setError('Bir hata olustu');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-dark-800 rounded-2xl w-full max-w-md p-6 border border-dark-700"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                type === 'sms' ? 'bg-green-500/20' : 'bg-blue-500/20'
              }`}>
                {type === 'sms' ? (
                  <Phone className={`w-5 h-5 text-green-400`} />
                ) : (
                  <Mail className={`w-5 h-5 text-blue-400`} />
                )}
              </div>
              <h2 className="text-xl font-bold text-white">
                {type === 'sms'
                  ? t('verification:phoneTitle', 'Telefon Dogrulama')
                  : t('verification:emailTitle', 'Email Dogrulama')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-dark-700 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          {step === 'send' && (
            <div className="text-center">
              <p className="text-gray-400 mb-6">
                {type === 'sms'
                  ? t('verification:phoneSendDesc', 'Telefon numaraniza dogrulama kodu gonderilecek.')
                  : t('verification:emailSendDesc', 'Email adresinize dogrulama kodu gonderilecek.')}
              </p>
              <button
                onClick={handleSendCode}
                disabled={sending}
                className={`w-full py-3 px-4 rounded-xl font-medium text-white transition-all duration-300 flex items-center justify-center gap-2 ${
                  type === 'sms'
                    ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-blue-500 hover:bg-blue-600'
                } disabled:opacity-50`}
              >
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('verification:sending', 'Gonderiliyor...')}
                  </>
                ) : (
                  t('verification:sendCode', 'Kod Gonder')
                )}
              </button>
            </div>
          )}

          {step === 'verify' && (
            <div>
              <p className="text-gray-400 text-center mb-2">
                {type === 'sms'
                  ? t('verification:phoneVerifyDesc', 'Telefonunuza gonderilen kodu girin')
                  : t('verification:emailVerifyDesc', 'Emailinize gonderilen kodu girin')}
              </p>
              <p className="text-primary-400 text-center text-sm mb-6">{target}</p>

              {/* Code Input */}
              <div className="flex justify-center gap-2 mb-4" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => { inputRefs.current[index] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold bg-dark-700 border border-dark-600 rounded-xl text-white focus:border-primary-500 focus:outline-none transition-colors"
                  />
                ))}
              </div>

              {error && (
                <p className="text-red-400 text-sm text-center mb-4">{error}</p>
              )}

              <button
                onClick={() => handleVerify()}
                disabled={loading || code.some((d) => !d)}
                className={`w-full py-3 px-4 rounded-xl font-medium text-white transition-all duration-300 flex items-center justify-center gap-2 ${
                  type === 'sms'
                    ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-blue-500 hover:bg-blue-600'
                } disabled:opacity-50`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('verification:verifying', 'Dogrulanıyor...')}
                  </>
                ) : (
                  t('verification:verify', 'Dogrula')
                )}
              </button>

              <button
                onClick={handleResendCode}
                disabled={countdown > 0 || sending}
                className="w-full mt-3 py-2 text-sm text-gray-400 hover:text-white disabled:text-gray-600 transition-colors"
              >
                {countdown > 0
                  ? t('verification:resendIn', 'Yeniden gonder ({{seconds}}s)', { seconds: countdown })
                  : t('verification:resend', 'Kodu tekrar gonder')}
              </button>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {t('verification:success', 'Basarili!')}
              </h3>
              <p className="text-gray-400">
                {type === 'sms'
                  ? t('verification:phoneVerified', 'Telefonunuz dogrulandi')
                  : t('verification:emailVerified', 'Emailiniz dogrulandi')}
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VerificationModal;
